th<template>
  <div>
    <div :id="chartId" class="container-chart">
      <h6 class="title-font mt-3" style="text-align: center;">{{ config.titles[$locale] }}</h6>
      <div class="lds-ripple" v-if="isDataLoading">
        <div></div>
        <div></div>
      </div>
      <chart-error :dataLoadedError="dataLoadedError" @reload="reloadData"></chart-error>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import d3Tip from 'd3-tip'
import moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'
import { DayTimeChartView } from '../../utils/charts/DayTimeChartView'
import { utilsMixin, chartMixin } from '../../utils/mixins'

export default {
  name: 'DayTimeChart',
  dependencies: ['dataService'],
  mixins: [utilsMixin, chartMixin],
  data() {
    return {
      moment: moment,
      dataset: { items: [] },
      sortedDataset: {},
      checked: [],
      savedStart: null,
      savedEnd: null,
      chartWidth: 1080,
      chartHeight: 275,
    }
  },
  props: ['config', 'chartId', 'period', 'mode', 'previewDataset', 'area'],
  mounted() {
    d3.tip = d3Tip
    momentDurationFormatSetup(moment)

    this.chart = new DayTimeChartView(
      this.chartId,
      this.sortedDataset,
      this.config.type,
      {
        width: this.chartWidth,
        heigh: this.chartHeight,
      },
      {
        top: 8,
        right: 20,
        bottom: 30,
        left: 50,
      },
      this.$t,
    )
    this.chart.initContainer(this.chartId, this.config.sort_by)
    this.chart.setTitles(this.config.titles[this.$locale])
    this.chart.setMode(this.mode)

    if (this.mode !== 'preview') {
      this.savedStart = this.period[0]
      this.savedEnd = this.period[1]
    } else {
      setTimeout(() => {
        //Set timeout for svg preloading
        this.dataset = this.previewDataset
        this.processData()
        this.displayChart()
      }, 500)
    }
  },
  beforeDestroy() {
    this.dataService.cancelDataRequest(this.currentRequestId)
  },
  components: {
    'chart-error': () => import('../utils/ChartErrorReload'),
  },
  watch: {
    config: {
      handler() {
        if (this.mode != 'preview' && this.hasToRequestNewData()) {
          this.loadConfigAndFetchData()
        } else {
          this.updateChartData()
          this.displayChart()
        }
      },
      immediate: true,
    },
    period: {
      handler: function(period) {
        if (this.mode != 'preview') {
          if (period[0] != this.savedStart || period[1] != this.savedEnd) {
            this.savedStart = this.period[0]
            this.savedEnd = this.period[1]
            this.loadConfigAndFetchData()
          }
        }
      },
      immediate: false,
    },
  },
  methods: {
    loadConfigAndFetchData: function() {
      this.requestCount++

      this.dataService
        .getChartData(
          this.config,
          this.period,
          this.dateFormat,
          this.getCurrentUser,
          this.currentRequestId,
          this.$props.area,
        )
        .then(data => {
          this.chart.setTitles(this.config.titles[this.$locale])
          this.$emit('update', {
            chart_id: this.config.id,
            data: JSON.parse(JSON.stringify(data.result)),
          })
          this.dataset = data.result
          this.processData()
          this.displayChart()
          this.dataLoadedError = false
          this.requestCount--
        })
        .catch(e => {
          if (e.message != 'cancel') {
            console.error('DayTimeChart error', e)
            this.dataLoadedError = true
          }
          this.requestCount--
        })

      this.currentRequestId = this.getRequestId(
        this.config,
        this.period,
        this.dateFormat,
        this.getCurrentUser,
      )
    },
    processData() {
      this.sortedDataset['lines'] = [
        { sort: 'start', items: [] },
        { sort: 'end', items: [] },
        { sort: 'affluence', items: [] },
      ]
      this.checked = ['start', 'end', 'affluence', this.$t('select_all')]
      this.dataset.items.forEach(item => {
        this.sortedDataset['lines'][0]['items'].push({
          date: item.date,
          value: item.value.start,
        })
        this.sortedDataset['lines'][1]['items'].push({
          date: item.date,
          value: item.value.end,
        })
        this.sortedDataset['lines'][2]['items'].push({
          date: item.date,
          value: item.affluence,
        })
      })

      this.dataset['lines'] = JSON.parse(JSON.stringify(this.sortedDataset.lines))

      this.updateChartData()
    },
    displayChart: function() {
      d3.select('#' + this.chartId + ' svg').remove()

      this.chart.initContainer(this.chartId)
      this.chart.initTooltip(this.otherCategories)
      this.chart.draw()
      this.chart.displayGrid()
    },
    updateChartData: function() {
      if (this.chart) {
        //If chart already displayed
        if (
          this.chart.sortedDataset &&
          this.chart.sortedDataset.lines &&
          this.chart.checked.length > 0
        ) {
          this.sortedDataset.lines = this.sortedDataset.lines.filter(line => {
            return this.chart.checked.indexOf(line.sort) > -1
          })
        } else {
          this.chart.setChecked(JSON.parse(JSON.stringify(this.checked)))
        }

        this.chart.setDataset(this.dataset)
        this.chart.setSortedDataset(this.sortedDataset)
        this.chart.setConfig(this.config)
      }
    },
  },
}
</script>
